import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";

type TaskUser = { id: number; name: string; email: string };

type DashboardPayload = {
  kpis: {
    openTotal: number;
    overdueTotal: number;
    dueTodayTotal: number;
    dueTomorrowTotal: number;
    completedPeriod: number;
  };
  overdue: Array<{
    id: number;
    titulo: string;
    deadline?: string | null;
    atribuidaPara?: TaskUser | null;
  }>;
  openByResponsible: Array<{
    userId?: number | null;
    count: number;
    user?: TaskUser | null;
  }>;
};

type KpiCard = {
  label: string;
  value: number;
  color: string;
  textColor: string;
};

export const TasksDashboardPage: React.FC = () => {
  const { client } = useAuth();
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    const response = await client.get("/tasks/dashboard");
    if (!response.ok) {
      setError("Falha ao carregar dashboard.");
      setLoading(false);
      return;
    }
    const payload = (await response.json()) as { data: DashboardPayload };
    setData(payload.data);
    setLoading(false);
  };

  useEffect(() => { void loadData(); }, []);

  const kpis: KpiCard[] = data
    ? [
        { label: "Abertas", value: data.kpis.openTotal, color: "#0d6efd", textColor: "text-primary" },
        { label: "Vencidas", value: data.kpis.overdueTotal, color: "#dc3545", textColor: "text-danger" },
        { label: "Vencem hoje", value: data.kpis.dueTodayTotal, color: "#fd7e14", textColor: "text-warning" },
        { label: "Vencem amanha", value: data.kpis.dueTomorrowTotal, color: "#ffc107", textColor: "" },
        { label: "Concluidas (7d)", value: data.kpis.completedPeriod, color: "#198754", textColor: "text-success" },
      ]
    : [];

  const chartData = (data?.openByResponsible ?? []).map((row) => ({
    name: row.user?.name ?? "Nao atribuido",
    count: row.count,
  }));

  const deadlineColor = (deadline: string | null | undefined) => {
    if (!deadline) return "";
    const d = new Date(deadline);
    const now = new Date();
    if (d < now) return "text-danger";
    const diff = (d.getTime() - now.getTime()) / 86_400_000;
    if (diff <= 1) return "text-warning";
    return "";
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="mb-0">Dashboard de Tarefas</h1>
        <button className="btn btn-outline-secondary btn-sm" onClick={loadData}>
          Atualizar
        </button>
      </div>

      <PageState loading={loading} error={error}>
        {data && (
          <>
            <div className="row g-3 mb-4">
              {kpis.map((k) => (
                <div className="col" key={k.label}>
                  <div className="card shadow-sm h-100" style={{ borderTop: `3px solid ${k.color}` }}>
                    <div className="card-body text-center">
                      <div className="text-muted small">{k.label}</div>
                      <div className={`display-6 fw-bold mt-1 ${k.textColor}`}>{k.value}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="row g-3">
              <div className="col-lg-7">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="card-title">Tarefas vencidas</h6>
                    <div className="table-responsive">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>Titulo</th>
                            <th>Responsavel</th>
                            <th>Deadline</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.overdue.map((task) => (
                            <tr key={task.id}>
                              <td>{task.titulo}</td>
                              <td>
                                <small className="text-muted">
                                  {task.atribuidaPara?.name ?? "—"}
                                </small>
                              </td>
                              <td className={deadlineColor(task.deadline)}>
                                {task.deadline ? task.deadline.slice(0, 10) : "—"}
                              </td>
                            </tr>
                          ))}
                          {data.overdue.length === 0 && (
                            <tr>
                              <td colSpan={3} className="text-center text-success">
                                Nenhuma tarefa vencida.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="card shadow-sm h-100">
                  <div className="card-body">
                    <h6 className="card-title">Abertas por responsavel</h6>
                    {chartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                          <Tooltip />
                          <Bar dataKey="count" name="Tarefas" radius={[0, 4, 4, 0]}>
                            {chartData.map((_, index) => (
                              <Cell
                                key={index}
                                fill={["#0d6efd","#6610f2","#20c997","#fd7e14","#0dcaf0"][index % 5]}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle">
                          <thead>
                            <tr>
                              <th>Responsavel</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td colSpan={2} className="text-center text-muted">Sem dados.</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </PageState>
    </div>
  );
};
